import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString) {
  if (!dateString || dateString === 'None') return '-';
  
  let date;
  
  // Check if it's DD-MM-YY format (e.g., "26-10-22")
  if (/^\d{1,2}-\d{1,2}-\d{2}$/.test(dateString)) {
    const parts = dateString.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
    let year = parseInt(parts[2], 10);
    // Convert 2-digit year to 4-digit (assume 2000s)
    year = year < 50 ? 2000 + year : 1900 + year;
    date = new Date(year, month, day);
  } 
  // Check if it's DD-MM-YYYY format (e.g., "26-10-2022")
  else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString)) {
    const parts = dateString.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    date = new Date(year, month, day);
  }
  // Otherwise assume ISO format or other parseable format
  else {
    date = new Date(dateString);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('nl-BE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

export function formatAddress(property) {
  if (!property) return '';
  return `${property.street} ${property.number}, ${property.postal_code} ${property.city}`;
}

export function getGoogleMapsUrl(property) {
  if (!property) return '#';
  const address = encodeURIComponent(formatAddress(property));
  return `https://www.google.com/maps/search/?api=1&query=${address}`;
}

export function getWazeUrl(property) {
  if (!property) return '#';
  const address = encodeURIComponent(formatAddress(property));
  return `https://waze.com/ul?q=${address}&navigate=yes`;
}

export function getInstallationTypeLabel(type) {
  const labels = {
    solar_panels: 'Zonnepanelen',
    battery: 'Batterij',
    ev_charger: 'Laadpaal',
    inverter: 'Omvormer'
  };
  return labels[type] || type;
}

export function getInstallationTypeColor(type) {
  const colors = {
    solar_panels: 'amber',
    battery: 'blue',
    ev_charger: 'green',
    inverter: 'purple'
  };
  return colors[type] || 'gray';
}

export function getInstallationTypeBadgeClass(type) {
  const classes = {
    solar_panels: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    battery: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    ev_charger: 'bg-green-500/20 text-green-400 border-green-500/30',
    inverter: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
  };
  return classes[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
}

export function getRoleLabel(role) {
  const labels = {
    admin: 'Beheerder',
    technician: 'Technicus',
    sales: 'Verkoop'
  };
  return labels[role] || role;
}

export function needsCleaning(lastCleaningDate) {
  if (!lastCleaningDate || lastCleaningDate === 'None') return true;
  
  let date;
  
  // Check if it's DD-MM-YY format
  if (/^\d{1,2}-\d{1,2}-\d{2}$/.test(lastCleaningDate)) {
    const parts = lastCleaningDate.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    let year = parseInt(parts[2], 10);
    year = year < 50 ? 2000 + year : 1900 + year;
    date = new Date(year, month, day);
  } 
  // Check if it's DD-MM-YYYY format
  else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(lastCleaningDate)) {
    const parts = lastCleaningDate.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    date = new Date(year, month, day);
  }
  else {
    date = new Date(lastCleaningDate);
  }
  
  if (isNaN(date.getTime())) return true;
  
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  return date < sixMonthsAgo;
}

export function isOldInstallation(installationYear, yearsThreshold = 5) {
  if (!installationYear) return false;
  const currentYear = new Date().getFullYear();
  return (currentYear - installationYear) > yearsThreshold;
}

/**
 * Format phone number to Belgian format: +32 485 54 20 88
 * Handles various input formats and normalizes them
 */
export function formatPhoneNumber(phone) {
  if (!phone) return '';
  
  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // If it starts with 00, replace with +
  if (cleaned.startsWith('00')) {
    cleaned = '+' + cleaned.substring(2);
  }
  
  // If it starts with 0 (Belgian local format), add +32
  if (cleaned.startsWith('0') && !cleaned.startsWith('00')) {
    cleaned = '+32' + cleaned.substring(1);
  }
  
  // If no + at start, assume Belgian number and add +32
  if (!cleaned.startsWith('+')) {
    // If it's 9 digits, it's likely a Belgian mobile without country code
    if (cleaned.length === 9) {
      cleaned = '+32' + cleaned;
    } else {
      cleaned = '+32' + cleaned;
    }
  }
  
  // Now format the number
  // Belgian format: +32 XXX XX XX XX (mobile) or +32 X XXX XX XX (landline)
  if (cleaned.startsWith('+32')) {
    const nationalNumber = cleaned.substring(3);
    
    // Mobile numbers (start with 4)
    if (nationalNumber.startsWith('4') && nationalNumber.length === 9) {
      return `+32 ${nationalNumber.substring(0, 3)} ${nationalNumber.substring(3, 5)} ${nationalNumber.substring(5, 7)} ${nationalNumber.substring(7, 9)}`;
    }
    
    // Landline or other formats - generic grouping
    if (nationalNumber.length >= 8) {
      // Group in pairs from the end, first group can be 2-3 digits
      const groups = [];
      let remaining = nationalNumber;
      
      while (remaining.length > 0) {
        if (remaining.length <= 3) {
          groups.unshift(remaining);
          break;
        }
        groups.unshift(remaining.slice(-2));
        remaining = remaining.slice(0, -2);
      }
      
      return `+32 ${groups.join(' ')}`;
    }
    
    return `+32 ${nationalNumber}`;
  }
  
  // For non-Belgian numbers, just add spaces every 2-3 digits
  const countryCode = cleaned.match(/^\+\d{1,3}/)?.[0] || '';
  const rest = cleaned.substring(countryCode.length);
  
  if (rest.length > 0) {
    const groups = [];
    let remaining = rest;
    
    while (remaining.length > 0) {
      if (remaining.length <= 3) {
        groups.unshift(remaining);
        break;
      }
      groups.unshift(remaining.slice(-2));
      remaining = remaining.slice(0, -2);
    }
    
    return `${countryCode} ${groups.join(' ')}`;
  }
  
  return cleaned;
}

// Normaliseer API foutmeldingen (Pydantic 422 geeft detail als array van objecten)
export function getErrorMessage(error, fallback = 'Er ging iets mis') {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join(', ');
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return fallback;
}
